import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/services/api';
import type { ApiError, AuthResponse, AuthUser, AccountType } from '@/types';

// Token storage keys. The ACCESS key is intentionally the same one api.ts reads
// directly from localStorage to attach `Authorization: Bearer`, so writing it here
// keeps the existing request interceptor working with zero changes there.
const ACCESS_TOKEN_KEY = 'tensormaps-token';
const REFRESH_TOKEN_KEY = 'tensormaps-refresh-token';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeTokens(access?: string, refresh?: string): void {
  try {
    if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    /* storage unavailable (private mode) - tokens stay in-memory only */
  }
}

function clearStoredTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function errorMessage(e: unknown, fallback: string): string {
  const err = e as Partial<ApiError> | undefined;
  return (err && typeof err.message === 'string' && err.message) || fallback;
}

// Normalize the server-asserted admin flag onto the user so client-side gating
// has a strict boolean to check. This is UX-only — the gateway remains the real
// gate — but keeping it a true boolean avoids treating truthy strings as admin.
function withAdminFlag(user: AuthUser | null | undefined, extra?: unknown): AuthUser | null {
  if (!user) return user ?? null;
  const topLevel = (extra as { is_admin?: unknown } | undefined)?.is_admin;
  const flag = user.is_admin ?? topLevel;
  return { ...user, is_admin: flag === true };
}

// The auth-service wraps a profile response as { status, user }. Accept that
// shape or a bare user object, returning the AuthUser (or null).
function unwrapUser(body: unknown): AuthUser | null {
  if (!body || typeof body !== 'object') return null;
  const nested = (body as { user?: AuthUser }).user;
  if (nested && typeof nested === 'object') return nested;
  return body as AuthUser;
}

export interface RegisterPayload {
  email: string;
  password: string;
  name?: string;
  account_type?: AccountType;
  company?: string;
}

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  status: AuthStatus;
  error: string | null;
  initialized: boolean;

  // Derived helper
  isAuthenticated: () => boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  loadMe: () => Promise<void>;
  updateProfile: (updates: { name?: string; company?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  initialize: () => Promise<void>;
  setSession: (res: AuthResponse) => void;
  handleSessionExpired: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      // Seed tokens from the dedicated localStorage keys so the very first render
      // already reflects a logged-in session before initialize() validates it.
      accessToken: readStorage(ACCESS_TOKEN_KEY),
      refreshToken: readStorage(REFRESH_TOKEN_KEY),
      status: readStorage(ACCESS_TOKEN_KEY) ? 'idle' : 'unauthenticated',
      error: null,
      initialized: false,

      isAuthenticated: () => !!get().user,

      setSession: (res) => {
        // The auth-service nests tokens under `tokens` and the profile under `user`.
        const access = res.tokens?.access_token;
        const refresh = res.tokens?.refresh_token;
        writeTokens(access, refresh);
        set({
          user: withAdminFlag(res.user ?? get().user, res),
          accessToken: access ?? null,
          refreshToken: refresh ?? null,
          status: 'authenticated',
          error: null,
        });
      },

      login: async (email, password) => {
        set({ status: 'loading', error: null });
        try {
          const res = await api.post<AuthResponse>('/auth/login', { email, password });
          get().setSession(res.data);
        } catch (e) {
          const message = errorMessage(e, 'Unable to sign in. Check your credentials and try again.');
          set({ status: 'unauthenticated', error: message });
          throw new Error(message);
        }
      },

      register: async (payload) => {
        set({ status: 'loading', error: null });
        try {
          const res = await api.post<AuthResponse>('/auth/register', payload);
          get().setSession(res.data);
        } catch (e) {
          const message = errorMessage(e, 'Unable to create your account. Please try again.');
          set({ status: 'unauthenticated', error: message });
          throw new Error(message);
        }
      },

      logout: async () => {
        const refreshToken = get().refreshToken ?? readStorage(REFRESH_TOKEN_KEY);
        try {
          // Best-effort server-side revocation; send the refresh token so the
          // server can revoke it. Ignore failures so logout always succeeds locally.
          await api.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {});
        } catch {
          /* ignore */
        }
        clearStoredTokens();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          status: 'unauthenticated',
          error: null,
        });
      },

      // Explicit refresh helper. api.ts also refreshes transparently on a 401,
      // but this is exposed per the auth-store action contract.
      refresh: async () => {
        const refreshToken = get().refreshToken ?? readStorage(REFRESH_TOKEN_KEY);
        if (!refreshToken) return false;
        try {
          const res = await api.post<AuthResponse>('/auth/refresh', { refresh_token: refreshToken });
          const access = res.data.tokens?.access_token ?? null;
          const refresh = res.data.tokens?.refresh_token ?? null;
          writeTokens(access ?? undefined, refresh ?? undefined);
          set({
            accessToken: access,
            refreshToken: refresh,
          });
          return true;
        } catch {
          get().handleSessionExpired();
          return false;
        }
      },

      loadMe: async () => {
        const res = await api.get<{ user?: AuthUser } | AuthUser>('/auth/me');
        // The auth-service nests the profile under `user` (same as login/register);
        // fall back to a bare body for older shapes. Reading res.data directly here
        // dropped is_admin and every other field (the object was {status,user}).
        const profile = unwrapUser(res.data);
        // A transparent refresh inside api.ts may have rotated the tokens; resync.
        set({
          user: withAdminFlag(profile),
          accessToken: readStorage(ACCESS_TOKEN_KEY),
          refreshToken: readStorage(REFRESH_TOKEN_KEY),
          status: 'authenticated',
        });
      },

      updateProfile: async (updates) => {
        const res = await api.put<{ user?: AuthUser } | AuthUser>('/auth/me', updates);
        const profile = unwrapUser(res.data);
        set((state) => ({
          // Prefer the server's canonical user; fall back to a local merge.
          user: withAdminFlag(profile) ?? (state.user ? { ...state.user, ...updates } : state.user),
        }));
      },

      changePassword: async (currentPassword, newPassword) => {
        await api.put('/auth/password', {
          current_password: currentPassword,
          new_password: newPassword,
        });
      },

      initialize: async () => {
        if (get().initialized) return;
        const token = readStorage(ACCESS_TOKEN_KEY);
        set({
          accessToken: token,
          refreshToken: readStorage(REFRESH_TOKEN_KEY),
        });
        if (token) {
          try {
            // api.ts will transparently refresh once on a 401 and retry.
            await get().loadMe();
          } catch {
            get().handleSessionExpired();
          }
        }
        set({
          initialized: true,
          status: get().user ? 'authenticated' : 'unauthenticated',
        });
      },

      handleSessionExpired: () => {
        clearStoredTokens();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          status: 'unauthenticated',
        });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'tensormaps-auth-store',
      // Only the user is persisted here; tokens live in their own localStorage keys
      // (read by api.ts) to keep a single source of truth for the bearer token.
      partialize: (state) => ({ user: state.user }),
    }
  )
);
